import {useCallback, useMemo} from 'react';
import {useSyncExternalStore} from 'use-sync-external-store/shim';

import {useOptionalAgentChatStore} from '../../agentChat/store/context';
import {
  selectActiveThread,
  selectActiveThreadId,
} from '../../agentChat/store/selectors';
import {
  type AgentChatCopyTarget,
  type AgentChatMentionKind,
  type AgentChatMentionVia,
  type AgentChatMessageVia,
  type AgentChatStepKind,
  type AgentChatTrackContext,
  getTrackContext,
  trackAgentChatInteraction,
} from '../../services/analytics/agentChatEvents';
import {useNavContext} from '../NavContextProvider';

/**
 * useTrackWBAgentPanel binds open/close/dock trackers to the current route
 * + chat.
 */
export function useTrackWBAgentPanel() {
  const base = useAgentChatTrackContext();

  const trackClosed = useCallback(() => {
    trackAgentChatInteraction({...base, action: 'closed'});
  }, [base]);

  const trackDocked = useCallback(() => {
    trackAgentChatInteraction({...base, action: 'docked'});
  }, [base]);

  const trackNavbarButtonViewed = useCallback(() => {
    trackAgentChatInteraction({...base, action: 'navbar_button_viewed'});
  }, [base]);

  const trackOpened = useCallback(() => {
    trackAgentChatInteraction({...base, action: 'opened'});
  }, [base]);

  const trackUndocked = useCallback(() => {
    trackAgentChatInteraction({...base, action: 'undocked'});
  }, [base]);

  return {
    trackClosed,
    trackDocked,
    trackNavbarButtonViewed,
    trackOpened,
    trackUndocked,
  };
}

/**
 * useTrackWBAgentChats binds chat lifecycle trackers to the current
 * route.
 */
export function useTrackWBAgentChats() {
  const base = useAgentChatTrackContext();

  const trackChatCreated = useCallback(() => {
    // A brand-new chat has no server identity yet, so omit chat_id.
    const {chat_id, ...route} = base;
    trackAgentChatInteraction({...route, action: 'chat_created'});
  }, [base]);

  const trackChatDeleted = useCallback(
    (deletedChatId: string) => {
      trackAgentChatInteraction({
        ...base,
        action: 'chat_deleted',
        chat_id: deletedChatId,
      });
    },
    [base]
  );

  const trackChatRenamed = useCallback(
    (renamedChatId: string) => {
      trackAgentChatInteraction({
        ...base,
        action: 'chat_renamed',
        chat_id: renamedChatId,
      });
    },
    [base]
  );

  const trackChatSwitched = useCallback(
    (nextChatId: string) => {
      trackAgentChatInteraction({
        ...base,
        action: 'chat_switched',
        chat_id: nextChatId,
      });
    },
    [base]
  );

  return {
    trackChatCreated,
    trackChatDeleted,
    trackChatRenamed,
    trackChatSwitched,
  };
}

/**
 * useTrackWBAgentComposer binds composer-input trackers to the current
 * route + chat.
 */
export function useTrackWBAgentComposer() {
  const base = useAgentChatTrackContext();

  const trackMentionAdded = useCallback(
    (via: AgentChatMentionVia, kind: AgentChatMentionKind) => {
      trackAgentChatInteraction({
        ...base,
        action: 'mention_added',
        mention_via: via,
        mention_kind: kind,
      });
    },
    [base]
  );

  const trackMentionRemoved = useCallback(
    (kind: AgentChatMentionKind) => {
      trackAgentChatInteraction({
        ...base,
        action: 'mention_removed',
        mention_kind: kind,
      });
    },
    [base]
  );

  const trackMessageSent = useCallback(
    (via: AgentChatMessageVia) => {
      trackAgentChatInteraction({
        ...base,
        action: 'message_sent',
        message_via: via,
      });
    },
    [base]
  );

  const trackSuggestionClicked = useCallback(
    (label: string) => {
      trackAgentChatInteraction({
        ...base,
        action: 'suggestion_clicked',
        suggestion_label: label,
      });
    },
    [base]
  );

  const trackTurnStopped = useCallback(() => {
    trackAgentChatInteraction({...base, action: 'turn_stopped'});
  }, [base]);

  return {
    trackMentionAdded,
    trackMentionRemoved,
    trackMessageSent,
    trackSuggestionClicked,
    trackTurnStopped,
  };
}

/**
 * useTrackWBAgentInspection binds message-action trackers (copy, feedback,
 * step disclosure) to the current route + chat.
 */
export function useTrackWBAgentInspection() {
  const base = useAgentChatTrackContext();

  const trackCopied = useCallback(
    (target: AgentChatCopyTarget) => {
      trackAgentChatInteraction({
        ...base,
        action: 'copied',
        copy_target: target,
      });
    },
    [base]
  );

  const trackFeedbackSubmitted = useCallback(
    (isPositive: boolean | null, hasReasoning: boolean) => {
      trackAgentChatInteraction({
        ...base,
        action: 'feedback_submitted',
        feedback_is_positive: isPositive,
        feedback_has_reasoning: hasReasoning,
      });
    },
    [base]
  );

  const trackStepCollapsed = useCallback(
    (kind: AgentChatStepKind, toolName?: string) => {
      trackAgentChatInteraction({
        ...base,
        action: 'step_collapsed',
        step_kind: kind,
        tool_name: toolName,
      });
    },
    [base]
  );

  const trackStepExpanded = useCallback(
    (kind: AgentChatStepKind, toolName?: string) => {
      trackAgentChatInteraction({
        ...base,
        action: 'step_expanded',
        step_kind: kind,
        tool_name: toolName,
      });
    },
    [base]
  );

  return {
    trackCopied,
    trackFeedbackSubmitted,
    trackStepCollapsed,
    trackStepExpanded,
  };
}

/**
 * useAgentChatTrackContext is the shared base for the domain hooks — it
 * snapshots route + chat context so each callback ships a consistent
 * payload (chat_id omitted outside AgentChatProvider).
 */
function useAgentChatTrackContext(): AgentChatTrackContext {
  const {params} = useNavContext();
  const chatId = useOptionalResolvedChatId();

  return useMemo(() => getTrackContext(params, chatId), [params, chatId]);
}

/**
 * useOptionalResolvedChatId returns the active chat's root turn id when known,
 * falling back to the active id (a local UUID before the first turn persists,
 * or the id being loaded mid-switch), or undefined when none is active or the
 * AgentChatStoreContext is missing.
 */
function useOptionalResolvedChatId(): string | undefined {
  // oxlint-disable-next-line wandb/zustand-use-store-selectors - not a zustand store.
  const store = useOptionalAgentChatStore();

  // NOTE: reads the active Thread but returns the resolved id primitive, so the
  // per-streamed-event Thread mutations don't force re-renders.
  const {subscribe, getSnapshot} = useMemo(
    () =>
      store
        ? {
            subscribe: store.subscribe,
            getSnapshot: () => {
              const state = store.getState();
              return (
                selectActiveThread(state)?.id ?? selectActiveThreadId(state)
              );
            },
          }
        : {subscribe: () => () => {}, getSnapshot: () => null},
    [store]
  );

  return useSyncExternalStore(subscribe, getSnapshot) ?? undefined;
}
